import { Tab, Tabs } from '@blueprintjs/core'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { CodeMirrorInput } from 'browser/components/atomic-elements/atoms/input/code-mirror-input'
import ComponentsMap from 'browser/components'
import { MultiMixinRenderer } from 'shared-libs/components/entity/multi-mixin-renderer'
import { SafeView } from 'shared-libs/components/view/safe-view'
import { Entity } from 'shared-libs/models/entity'

const ENTITY_DETAIL_UI_SCHEMA_PATH = 'uiSchema.web.entityDetailSection'

/** Error-boundary fallback when the detail view fails to render. */
function previewUnavailable() {
  return <div className="c-placeholderColor">(entity preview unavailable — see JSON)</div>
}

/** Renders a transient (unsaved) entity's detail section, resolving its schemas read-only first. */
class EntityDetail extends React.Component<
  { content: any },
  { model: Entity | null; status: 'loading' | 'ready' | 'failed' }
> {
  private isMounted_ = false

  constructor(props: EntityDetail['props']) {
    super(props)
    this.state = { model: null, status: 'loading' }
  }

  public componentDidMount() {
    this.isMounted_ = true
    apis
      .getStore()
      .resolveRecords([_.cloneDeep(this.props.content)], /* shouldCache */ false)
      .then(([model]) => {
        if (this.isMounted_) {
          this.setState({ model, status: 'ready' })
        }
      })
      .catch(() => {
        if (this.isMounted_) {
          this.setState({ status: 'failed' })
        }
      })
  }

  public componentWillUnmount() {
    this.isMounted_ = false
  }

  public render() {
    const { model, status } = this.state
    if (status === 'loading') {
      return <div className="c-placeholderColor u-bumperTop--xs">Rendering entity…</div>
    }
    if (status === 'failed' || !model) {
      // Fall back silently; the JSON tab still shows the payload.
      return null
    }
    // Full-width wrapper; the section's flex parent otherwise collapses its height.
    return (
      <div
        className="w-100 u-bumperTop--xs"
        style={{ border: '1px solid #e0e0e0', borderRadius: 3, padding: 8 }}
      >
        <MultiMixinRenderer
          value={model}
          componentsMap={ComponentsMap}
          uiSchemaPath={ENTITY_DETAIL_UI_SCHEMA_PATH}
          // Only the most-derived mixin's detail section, so multiple mixins don't repeat it.
          shouldOverrideUiSchema={true}
          context={{ density: 'collapse', isHorizontalLayout: true, isStatic: true }}
          state={{ settings: apis.getSettings() }}
          actions={this}
          isEditable={false}
          onChangeComplete={_.noop}
        />
      </div>
    )
  }
}

/**
 * Read-only detail + JSON view of a transient (unsaved) entity, without EntityDetailCard's
 * persisted-entity chrome. Give it a `key` tied to the entity so selection changes re-resolve.
 */
export function TransientEntityDetailCard({ content }: { content: any }) {
  return (
    <div className="u-flexColumn" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 10 }}>
        <Tabs
          id="transientEntityDetail"
          defaultSelectedTabId="details"
          renderActiveTabPanelOnly={true}
        >
          <Tab
            id="details"
            title="Details"
            panel={
              content && (
                <SafeView errorInvalidationKey={content} renderError={previewUnavailable}>
                  <EntityDetail content={content} />
                </SafeView>
              )
            }
          />
          <Tab
            id="json"
            title="JSON"
            panel={
              <CodeMirrorInput
                value={JSON.stringify(content, null, 2)}
                language="json"
                readOnly={true}
                onChange={_.noop}
              />
            }
          />
        </Tabs>
      </div>
    </div>
  )
}
